<template>
  <div class="ds relative h-screen w-full overflow-hidden bg-[#050508] text-[11px] text-gray-200">
    <canvas
      ref="canvasEl"
      class="ds-canvas absolute inset-0 block h-full w-full touch-none outline-none"
      @pointerdown="onCanvasPointerDown"
      @contextmenu.prevent
    />

    <!-- 镜头预览小窗 + 三分法安全框 -->
    <div
      class="pointer-events-none absolute z-[15] overflow-hidden rounded-lg border border-white/20 shadow-2xl"
      :style="previewWrapStyle"
    >
      <div class="shot-thirds pointer-events-none absolute inset-0 opacity-70" />
      <p class="pointer-events-none absolute bottom-1 left-1 right-1 text-center text-[8px] text-gray-400">
        导出取景 · {{ shotAspect }}（与主画布一致）
      </p>
    </div>

    <!-- HUD -->
    <div
      class="pointer-events-none absolute left-3 top-14 z-[12] max-w-[min(92vw,320px)] space-y-1 rounded-xl border border-white/[0.08] bg-black/55 px-3 py-2 font-mono text-[9px] text-gray-300 backdrop-blur-sm md:left-5 md:top-16"
    >
      <p class="text-violet-200/90">主视图运镜 t={{ playTime.toFixed(2) }}s / {{ duration.toFixed(1) }}s</p>
      <p>pos {{ hud.pos }}</p>
      <p>tgt {{ hud.tgt }}</p>
      <p>FOV {{ hud.fov }}° · 帧 {{ hud.frame }}</p>
      <p v-if="hud.warn" class="text-amber-300/95">{{ hud.warn }}</p>
    </div>

    <header
      class="pointer-events-auto absolute left-3 right-3 top-3 z-20 flex flex-wrap items-center gap-2 md:left-5 md:right-[308px]"
    >
      <button
        type="button"
        class="h-8 rounded-lg border border-white/10 bg-white/[0.06] px-2.5 text-[11px] hover:bg-white/[0.1]"
        @click="goBackFromDirector"
      >
        <i class="fas fa-arrow-left mr-1 text-[10px]"></i>返回
      </button>
      <button
        v-if="isOpenedFromNodeGraph"
        type="button"
        class="h-8 rounded-lg border border-emerald-500/40 bg-emerald-600/80 px-2.5 text-[11px] font-semibold text-white shadow hover:bg-emerald-500/90 disabled:cursor-not-allowed disabled:opacity-40"
        :disabled="ngcSaveReturnBusy"
        :title="ngcSaveReturnTitle"
        @click="saveAndReturnFromNodeCanvas"
      >
        <i class="fas fa-save mr-1 text-[10px] opacity-90"></i>保存返回
      </button>
      <h1 class="text-sm font-bold text-white md:text-base">3D 导演台</h1>
      <span class="text-[9px] text-gray-500">单位：米 · 关节假人默认身高 1.7m</span>
    </header>

    <!-- 右侧主面板 -->
    <aside
      class="pointer-events-auto absolute right-3 top-14 z-20 flex max-h-[calc(100vh-3.5rem)] w-[min(100%,292px)] flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0a0a0e]/95 shadow-2xl backdrop-blur-md md:right-4 md:top-16 md:w-[300px]"
    >
      <div class="flex-1 overflow-y-auto p-2.5 pb-24">
        <section class="mb-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-2">
          <p class="mb-1.5 text-[10px] font-medium text-gray-400">场景</p>
          <div class="flex flex-wrap gap-1.5">
            <button
              type="button"
              class="h-7 rounded-lg px-2 text-[10px]"
              :class="showGrid ? 'bg-emerald-600/30 text-emerald-100' : 'bg-white/[0.06] text-gray-400'"
              @click="showGrid = !showGrid; syncHelpers()"
            >
              网格
            </button>
            <button
              type="button"
              class="h-7 rounded-lg px-2 text-[10px]"
              :class="showAxes ? 'bg-emerald-600/30 text-emerald-100' : 'bg-white/[0.06] text-gray-400'"
              @click="showAxes = !showAxes; syncHelpers()"
            >
              坐标轴
            </button>
            <button
              type="button"
              class="h-7 rounded-lg px-2 text-[10px]"
              :class="lockScene ? 'bg-rose-600/35 text-rose-100' : 'bg-white/[0.06] text-gray-400'"
              @click="toggleLock"
            >
              {{ lockScene ? '已锁定' : '锁定场景' }}
            </button>
          </div>
          <input
            ref="panoramaFileInput"
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.hdr,image/jpeg,image/png,image/webp"
            class="fixed left-0 top-0 h-px w-px opacity-0"
            tabindex="-1"
            aria-hidden="true"
            @change="onPanoramaFileChange"
          />
          <div class="mt-2 flex flex-wrap gap-1.5">
            <button
              type="button"
              class="h-7 flex-1 rounded-lg bg-cyan-700/75 px-2 text-[10px] font-medium text-white hover:bg-cyan-600/90 disabled:opacity-35"
              :disabled="lockScene"
              @click="openPanoramaPicker"
            >
              导入 360° 全景
            </button>
            <button
              v-if="panoramaFileName"
              type="button"
              class="h-7 shrink-0 rounded-lg border border-white/15 px-2 text-[10px] text-gray-300 hover:bg-white/[0.06] disabled:opacity-35"
              :disabled="lockScene"
              @click="clearPanorama"
            >
              清除
            </button>
          </div>
          <p v-if="panoramaFileName" class="mt-1 truncate text-[9px] text-cyan-200/80" :title="panoramaFileName">{{ panoramaFileName }}</p>
          <p class="mt-1 text-[9px] leading-snug text-gray-600">
            支持等距柱状投影（约 2:1 最佳，否则球面映射会变形、显得被放大或压扁；HDR 用 .hdr）。导入后会略增编辑视野；HDR 略降曝光；同时隐藏地面/网格、关闭雾效。
          </p>
        </section>

        <section class="mb-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-2">
          <p class="mb-1.5 text-[10px] font-medium text-gray-400">添加（放置于视点前地面）</p>
          <div class="flex flex-wrap gap-1.5">
            <button
              type="button"
              class="h-8 flex-1 rounded-lg bg-blue-600/80 px-2 text-[10px] font-semibold text-white disabled:opacity-35"
              :disabled="lockScene"
              @click="addCharacter"
            >
              + 假人
            </button>
            <button
              type="button"
              class="h-8 flex-1 rounded-lg bg-amber-700/80 px-2 text-[10px] font-semibold text-white disabled:opacity-35"
              :disabled="lockScene"
              @click="addCube"
            >
              + 立方体
            </button>
          </div>
          <p class="mt-1 text-[9px] leading-snug text-gray-600">场景中左键拖实体：在过物体的<strong class="text-gray-400">竖直拖拽面</strong>上移动（上下改 Y、左右前后改 XZ，随相机方位变化）；假人可拖上臂/小臂/腿调关节。选中后在侧栏改精确数值。</p>
        </section>

        <section class="mb-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-2">
          <p class="mb-1 text-[10px] font-medium text-gray-400">实体列表</p>
          <div v-if="!entityPanel.length" class="py-2 text-center text-[10px] text-gray-600">暂无实体</div>
          <button
            v-for="e in entityPanel"
            :key="e.id"
            type="button"
            class="mb-1 flex w-full items-center justify-between rounded-lg border px-2 py-1.5 text-left text-[10px] transition"
            :class="
              selectedId === e.id
                ? 'border-violet-500/50 bg-violet-600/15 text-violet-100'
                : 'border-white/[0.06] bg-black/30 hover:border-white/15'
            "
            @click="selectEntity(e.id)"
          >
            <span class="flex min-w-0 items-center gap-1.5 truncate">
              <span
                v-if="e.type === 'character'"
                class="inline-block h-2.5 w-2.5 shrink-0 rounded-sm border border-white/25"
                :style="{ backgroundColor: e.bodyColorCss }"
              ></span>
              <span v-else class="shrink-0 text-amber-200">●</span>
              <span :class="e.type === 'character' ? 'text-sky-100' : 'text-amber-100'" class="truncate">{{ e.label }}</span>
            </span>
            <span class="shrink-0 font-mono text-[9px] text-gray-500">{{ e.type }}</span>
          </button>
          <button
            type="button"
            class="mt-1 h-7 w-full rounded-lg border border-white/10 text-[10px] text-rose-300/90 hover:bg-rose-950/40 disabled:opacity-35"
            :disabled="!selectedId || lockScene"
            @click="removeSelected"
          >
            删除选中
          </button>
        </section>

        <section v-if="selectedId" class="mb-2 rounded-xl border border-violet-500/25 bg-violet-950/20 p-2">
          <p class="mb-1.5 text-[10px] font-medium text-violet-200">选中 · 滑块拖动（米 / 度）</p>
          <div class="space-y-1.5">
            <div>
              <div class="flex justify-between text-[8px] text-gray-500"><span>X</span><span class="font-mono text-gray-400">{{ edit.x.toFixed(2) }}m</span></div>
              <input v-model.number="edit.x" type="range" min="-9" max="9" step="0.05" class="ds-slider mt-0.5 w-full" :disabled="lockScene" @input="applyEditTransform" />
            </div>
            <div>
              <div class="flex justify-between text-[8px] text-gray-500"><span>Y</span><span class="font-mono text-gray-400">{{ edit.y.toFixed(2) }}m</span></div>
              <input v-model.number="edit.y" type="range" min="-0.5" max="14" step="0.05" class="ds-slider mt-0.5 w-full" :disabled="lockScene" @input="applyEditTransform" />
            </div>
            <div>
              <div class="flex justify-between text-[8px] text-gray-500"><span>Z</span><span class="font-mono text-gray-400">{{ edit.z.toFixed(2) }}m</span></div>
              <input v-model.number="edit.z" type="range" min="-9" max="9" step="0.05" class="ds-slider mt-0.5 w-full" :disabled="lockScene" @input="applyEditTransform" />
            </div>
          </div>
          <div class="mt-2 space-y-1.5">
            <div>
              <div class="flex justify-between text-[8px] text-gray-500"><span>Yaw ° 水平转</span><span class="font-mono text-gray-400">{{ Math.round(edit.yawDeg) }}°</span></div>
              <input v-model.number="edit.yawDeg" type="range" min="-180" max="180" step="1" class="ds-slider mt-0.5 w-full" :disabled="lockScene" @input="applyEditTransform" />
            </div>
            <div>
              <div class="flex justify-between text-[8px] text-gray-500"><span>Pitch ° 俯仰</span><span class="font-mono text-gray-400">{{ Math.round(edit.pitchDeg) }}°</span></div>
              <input v-model.number="edit.pitchDeg" type="range" min="-90" max="90" step="1" class="ds-slider mt-0.5 w-full" :disabled="lockScene" @input="applyEditTransform" />
            </div>
          </div>
          <template v-if="edit.type === 'character'">
            <div class="mt-2">
              <div class="flex justify-between text-[8px] text-gray-500"><span>名称</span></div>
              <input
                v-model="edit.label"
                type="text"
                maxlength="48"
                placeholder="显示在列表与导出 JSON"
                class="mt-0.5 w-full rounded border border-white/10 bg-black/40 px-2 py-1 text-[10px] text-gray-200 outline-none focus:border-violet-500/50"
                :disabled="lockScene"
                @change="applyCharacterLabel"
              />
            </div>
            <div class="mt-2 flex items-center gap-2">
              <span class="shrink-0 text-[8px] text-gray-500">身体颜色</span>
              <input
                v-model="edit.bodyColor"
                type="color"
                class="h-8 w-14 cursor-pointer rounded border border-white/10 bg-transparent p-0.5"
                :disabled="lockScene"
                @input="applyCharacterBodyColor"
              />
            </div>
            <div class="mt-2">
              <div class="flex justify-between text-[8px] text-gray-500"><span>身高 m</span><span class="font-mono text-gray-400">{{ edit.height.toFixed(2) }}</span></div>
              <input v-model.number="edit.height" type="range" min="0.6" max="2.4" step="0.02" class="ds-slider mt-0.5 w-full" :disabled="lockScene" @input="applyCharacterHeight" />
            </div>
            <label class="mt-1.5 flex items-center gap-2 text-[10px] text-gray-400">
              <input v-model="edit.billboard" type="checkbox" @change="applyEditTransform" />始终面向活动镜头（Billboard）
            </label>
            <p class="mt-2 text-[9px] font-medium text-sky-200/90">假人关节（°）</p>
            <p class="mb-1 text-[8px] leading-snug text-gray-600">也可在画布上<strong class="text-gray-400">直接拖拽</strong>肢体；下方为进度条式调节。</p>
            <div class="mt-1 space-y-1.5">
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>脊柱前倾</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.spineX) }}°</span></div>
                <input v-model.number="edit.pose.spineX" type="range" min="-55" max="55" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>颈俯仰</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.neckX) }}°</span></div>
                <input v-model.number="edit.pose.neckX" type="range" min="-70" max="70" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>颈左右</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.neckY) }}°</span></div>
                <input v-model.number="edit.pose.neckY" type="range" min="-90" max="90" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
            </div>
            <p class="mt-2 text-[8px] text-gray-500">左臂</p>
            <div class="space-y-1.5">
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>肩 X</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.armLX) }}°</span></div>
                <input v-model.number="edit.pose.armLX" type="range" min="-90" max="90" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>肩 Z</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.armLZ) }}°</span></div>
                <input v-model.number="edit.pose.armLZ" type="range" min="-120" max="120" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>肘</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.armLElbowX) }}°</span></div>
                <input v-model.number="edit.pose.armLElbowX" type="range" min="0" max="170" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
            </div>
            <p class="mt-2 text-[8px] text-gray-500">右臂</p>
            <div class="space-y-1.5">
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>肩 X</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.armRX) }}°</span></div>
                <input v-model.number="edit.pose.armRX" type="range" min="-90" max="90" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>肩 Z</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.armRZ) }}°</span></div>
                <input v-model.number="edit.pose.armRZ" type="range" min="-120" max="120" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>肘</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.armRElbowX) }}°</span></div>
                <input v-model.number="edit.pose.armRElbowX" type="range" min="0" max="170" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
            </div>
            <p class="mt-2 text-[8px] text-gray-500">左腿 / 右腿</p>
            <div class="space-y-1.5">
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>左髋</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.legLX) }}°</span></div>
                <input v-model.number="edit.pose.legLX" type="range" min="-80" max="80" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>左膝</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.legLKneeX) }}°</span></div>
                <input v-model.number="edit.pose.legLKneeX" type="range" min="0" max="170" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>右髋</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.legRX) }}°</span></div>
                <input v-model.number="edit.pose.legRX" type="range" min="-80" max="80" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
              <div>
                <div class="flex justify-between text-[8px] text-gray-500"><span>右膝</span><span class="font-mono text-gray-400">{{ Math.round(edit.pose.legRKneeX) }}°</span></div>
                <input v-model.number="edit.pose.legRKneeX" type="range" min="0" max="170" step="1" class="ds-slider w-full" :disabled="lockScene" @input="applyMannequinPose" />
              </div>
            </div>
            <button type="button" class="mt-1.5 h-7 w-full rounded border border-white/10 text-[9px] text-gray-300 hover:bg-white/[0.06]" :disabled="lockScene" @click="resetMannequinPose">重置关节姿势</button>
          </template>
          <div class="mt-2 flex flex-wrap gap-1">
            <button type="button" class="h-7 rounded bg-white/[0.08] px-2 text-[9px]" :disabled="lockScene" @click="alignFeet">双脚贴地</button>
            <button type="button" class="h-7 rounded bg-white/[0.08] px-2 text-[9px]" :disabled="lockScene" @click="faceShotCamera">面朝镜头</button>
          </div>
        </section>

        <section class="mb-2 rounded-xl border border-white/[0.06] bg-white/[0.02] p-2">
          <p class="mb-1 text-[10px] font-medium text-gray-400">导出机位（与主画布同步）</p>
          <p class="mb-1.5 text-[9px] leading-snug text-gray-600">
            场景中<strong class="font-medium text-gray-400">旋转/平移主视图</strong>即为取景；右上角小窗与导出 JSON 使用同一机位。不再显示独立机位模型。
          </p>
          <p class="rounded border border-white/[0.06] bg-black/35 px-1.5 py-1 font-mono text-[8px] leading-relaxed text-gray-400">
            P {{ shotEdit.px.toFixed(2) }}, {{ shotEdit.py.toFixed(2) }}, {{ shotEdit.pz.toFixed(2) }}<br />
            T {{ shotEdit.tx.toFixed(2) }}, {{ shotEdit.ty.toFixed(2) }}, {{ shotEdit.tz.toFixed(2) }}
          </p>
          <div class="mt-2">
            <div class="flex justify-between text-[9px] text-gray-500"><span>FOV°（主视图）</span><span class="font-mono text-gray-400">{{ Math.round(shotEdit.fov) }}°</span></div>
            <input v-model.number="shotEdit.fov" type="range" min="10" max="100" step="1" class="ds-slider mt-0.5 w-full" :disabled="lockScene" @input="applyExportFovToEditor" />
          </div>
          <label class="mt-1.5 block text-[9px] text-gray-500"
            >画幅
            <select v-model="shotAspect" class="mt-0.5 w-full rounded border border-white/10 bg-black/40 px-1 py-1 text-[10px]" @change="onShotAspectChange">
              <option value="16:9">16:9</option>
              <option value="4:3">4:3</option>
              <option value="9:16">9:16</option>
              <option value="1:1">1:1</option>
            </select>
          </label>
        </section>

        <section class="rounded-xl border border-white/[0.06] bg-white/[0.02] p-2">
          <p class="mb-1 text-[10px] font-medium text-gray-400">运镜关键帧（缓入缓出）</p>
          <div v-for="(kf, idx) in keyframes" :key="idx" class="mb-1 rounded border border-white/[0.05] bg-black/30 p-1.5 font-mono text-[9px] text-gray-400">
            <div class="flex items-center justify-between text-gray-300">
              <span>#{{ idx + 1 }}</span>
              <button type="button" class="text-rose-400/90 disabled:opacity-30" :disabled="lockScene || keyframes.length <= 2" @click="removeKeyframe(idx)">删</button>
            </div>
            <div class="mt-0.5">
              <div class="flex justify-between text-[8px] text-gray-500"><span>t(s)</span><span class="font-mono text-gray-400">{{ Number(kf.time || 0).toFixed(1) }}</span></div>
              <input v-model.number="kf.time" type="range" min="0" :max="Math.max(duration, 0.5)" step="0.05" class="ds-slider w-full" :disabled="lockScene" @input="rebuildPath" />
            </div>
          </div>
          <button type="button" class="mt-1 h-7 w-full rounded-lg bg-white/[0.08] text-[10px] disabled:opacity-35" :disabled="lockScene" @click="addKeyframe">+ 关键帧（复制当前镜头）</button>
          <div class="mt-2">
            <label class="text-[9px] text-gray-500">时间轴 scrub</label>
            <input v-model.number="playTime" type="range" :min="0" :max="duration" :step="0.01" class="mt-1 w-full accent-violet-500" :disabled="playing" @input="onScrub" />
          </div>
          <div class="mt-2 flex gap-1">
            <button type="button" class="h-8 flex-1 rounded-lg bg-violet-600/85 text-[10px] font-semibold text-white disabled:opacity-35" :disabled="lockScene" @click="togglePlay">
              {{ playing ? '暂停' : '播放' }}
            </button>
            <button type="button" class="h-8 flex-1 rounded-lg bg-white/[0.1] text-[10px]" @click="syncKeyframesFromShot">写入当前到最近关键帧</button>
          </div>
        </section>
      </div>

      <div class="absolute bottom-0 left-0 right-0 flex flex-col gap-1.5 border-t border-white/[0.08] bg-[#08080c]/98 p-2">
        <button
          type="button"
          class="h-9 w-full rounded-xl bg-emerald-600/90 text-[11px] font-bold text-white shadow-lg hover:bg-emerald-500"
          @click="exportJson"
        >
          导出 JSON（相机 + 实体 + 帧序列）
        </button>
        <button
          type="button"
          class="h-8 w-full rounded-xl border border-white/15 bg-white/[0.08] text-[10px] font-medium text-gray-200 hover:bg-white/[0.12]"
          @click="exportPreviewPng"
        >
          导出预览图 PNG（导出机位 · 长边 1920）
        </button>
      </div>
    </aside>
  </div>
</template>

<script>
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import { buildDirectorExportPayload, downloadJson, interpolateShotKeyframes } from '@/utils/directorStudioExport.js'
import { uploadVideoMediaFile } from '@/utils/videoImageApi'
import { showErrorToast } from '@/utils/toast'

/** 与 NodeGraphCanvas 一致：导出 PNG 后回写图片节点 */
const NGC_DIRECTOR_PNG_SESSION_KEY = 'ngc_director_png_v1'
const NGC_LAST_OPENED_CANVAS_KEY = 'ngc_last_opened_canvas'

const DEF_CHAR_H = 1.7
const BOUNDS = 9
/** 无全景时编辑相机竖直视场（°） */
const DEFAULT_EDITOR_FOV = 48
/** 有全景时略增视野，减轻「全景被放大」的观感 */
const PANORAMA_EDITOR_FOV = 58

function disposeObject3D(root) {
  root.traverse(obj => {
    if (obj.geometry) obj.geometry.dispose()
    if (obj.material) {
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material]
      mats.forEach(m => {
        if (m && m.map) m.map.dispose()
        if (m) m.dispose()
      })
    }
  })
}

function clearEntityPickHighlight(en) {
  const mats = []
  if (en.bodyMat) mats.push(en.bodyMat)
  if (en.headMat) mats.push(en.headMat)
  if (en.type === 'cube' && en.mesh && en.mesh.material) mats.push(en.mesh.material)
  mats.forEach(em => {
    if (em && em.emissive) {
      em.emissive.setHex(0)
      em.emissiveIntensity = 0
    }
  })
}

/** 实体根旋转：Yaw(Y) + Pitch(X)，Euler 顺序 YXZ */
function readEntityYawPitchDeg(group) {
  group.rotation.order = 'YXZ'
  return {
    yawDeg: (group.rotation.y * 180) / Math.PI,
    pitchDeg: (group.rotation.x * 180) / Math.PI
  }
}

function writeEntityYawPitchDeg(group, yawDeg, pitchDeg) {
  group.rotation.order = 'YXZ'
  group.rotation.y = ((Number(yawDeg) || 0) * Math.PI) / 180
  group.rotation.x = ((Number(pitchDeg) || 0) * Math.PI) / 180
  group.rotation.z = 0
}

/** 射线命中网格向上追溯时是否经过可拖拽肢体关节 */
function hitHasLimbDragAncestor(obj) {
  let o = obj
  while (o) {
    if (o.userData && o.userData.limbDrag) return true
    o = o.parent
  }
  return false
}

/** 四肢关节角度（度），与 setRigPose 字段一致 */
/** 假人身体色：与 `<input type="color">` 一致 #RRGGBB */
function bodyColorCssFromHex(hex) {
  const n = Math.max(0, Math.min(0xffffff, Number(hex) || 0))
  return '#' + n.toString(16).padStart(6, '0')
}

function parseBodyColorInput(str) {
  if (str == null || str === '') return null
  const s = String(str).trim().replace(/^#/, '')
  if (!/^[0-9a-fA-F]{6}$/.test(s)) return null
  return parseInt(s, 16)
}

function defaultMannequinPose() {
  return {
    spineX: 0,
    neckX: 0,
    neckY: 0,
    armLX: 0,
    armLZ: 0,
    armLElbowX: 0,
    armRX: 0,
    armRZ: 0,
    armRElbowX: 0,
    legLX: 0,
    legLKneeX: 0,
    legRX: 0,
    legRKneeX: 0
  }
}

function setRigPose(rig, pose) {
  if (!rig) return
  const d2r = THREE.MathUtils.degToRad
  const p = { ...defaultMannequinPose(), ...(pose || {}) }
  rig.spine.rotation.x = d2r(p.spineX)
  rig.neck.rotation.x = d2r(p.neckX)
  rig.neck.rotation.y = d2r(p.neckY)
  rig.lHip.rotation.x = d2r(p.legLX)
  rig.lKnee.rotation.x = d2r(p.legLKneeX)
  rig.rHip.rotation.x = d2r(p.legRX)
  rig.rKnee.rotation.x = d2r(p.legRKneeX)
  rig.armLU.rotation.x = d2r(p.armLX)
  rig.armLU.rotation.z = d2r(p.armLZ)
  rig.armLL.rotation.x = d2r(p.armLElbowX)
  rig.armRU.rotation.x = d2r(p.armRX)
  rig.armRU.rotation.z = d2r(p.armRZ)
  rig.armRL.rotation.x = d2r(p.armRElbowX)
}

/**
 * 可摆姿势的关节假人（骨盆固定、脊柱/颈/四肢为旋转关节）
 * @returns {{ group: THREE.Group, bodyMat: THREE.Material, headMat: THREE.Material, height: number, rig: object, pose: object }}
 */
function makeCharacter(id, height, color, poseIn) {
  const REF = 1.7
  const s = height / REF
  const pose = { ...defaultMannequinPose(), ...(poseIn || {}) }

  const bodyMat = new THREE.MeshStandardMaterial({
    color: color || 0x3b82f6,
    roughness: 0.78,
    metalness: 0.08
  })
  const headMat = new THREE.MeshStandardMaterial({
    color: 0xdbeafe,
    roughness: 0.68,
    metalness: 0.04
  })

  const limbW = 0.088 * s
  const footH = 0.048 * s
  const shinL = 0.375 * s
  const thighL = 0.4 * s
  const legTop = footH + shinL + thighL
  const hipX = 0.095 * s
  const pelvisH = 0.1 * s
  const torsoH = 0.34 * s
  const uArmLen = 0.27 * s
  const lArmLen = 0.25 * s
  const headR = 0.098 * s

  const group = new THREE.Group()
  group.userData.entityId = id
  group.userData.pickable = true
  group.rotation.order = 'YXZ'

  const rig = new THREE.Group()
  rig.name = 'rig'
  group.add(rig)

  const markPick = mesh => {
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.userData.pickable = true
  }

  const boxMesh = (w, h, d, mat) => {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat)
    markPick(m)
    return m
  }

  const lHip = new THREE.Group()
  lHip.position.set(-hipX, legTop, 0)
  const lThigh = boxMesh(limbW, thighL, limbW * 0.92, bodyMat)
  lThigh.position.y = -thighL / 2
  const lKnee = new THREE.Group()
  lKnee.position.y = -thighL
  const lShin = boxMesh(limbW * 0.94, shinL, limbW * 0.9, bodyMat)
  lShin.position.y = -shinL / 2
  const lFoot = boxMesh(limbW * 1.15, footH, limbW * 1.38, bodyMat)
  lFoot.position.y = -shinL - footH / 2
  lKnee.add(lShin, lFoot)
  lHip.add(lThigh, lKnee)

  const rHip = new THREE.Group()
  rHip.position.set(hipX, legTop, 0)
  const rThigh = boxMesh(limbW, thighL, limbW * 0.92, bodyMat)
  rThigh.position.y = -thighL / 2
  const rKnee = new THREE.Group()
  rKnee.position.y = -thighL
  const rShin = boxMesh(limbW * 0.94, shinL, limbW * 0.9, bodyMat)
  rShin.position.y = -shinL / 2
  const rFoot = boxMesh(limbW * 1.15, footH, limbW * 1.38, bodyMat)
  rFoot.position.y = -shinL - footH / 2
  rKnee.add(rShin, rFoot)
  rHip.add(rThigh, rKnee)

  const pelvis = boxMesh(0.26 * s, pelvisH, 0.16 * s, bodyMat)
  pelvis.position.set(0, legTop + pelvisH / 2, 0)

  const spine = new THREE.Group()
  spine.position.set(0, legTop + pelvisH, 0)
  const chest = boxMesh(0.22 * s, torsoH, 0.14 * s, bodyMat)
  chest.position.y = torsoH / 2
  spine.add(chest)

  const neck = new THREE.Group()
  neck.position.set(0, torsoH - 0.02 * s, 0)
  const head = new THREE.Mesh(new THREE.SphereGeometry(headR, 14, 10), headMat)
  markPick(head)
  head.position.y = headR * 0.92
  neck.add(head)
  spine.add(neck)

  const shoulderY = torsoH * 0.74
  const shoulderXOff = 0.12 * s + limbW * 0.55

  const armLU = new THREE.Group()
  armLU.position.set(-shoulderXOff, shoulderY, 0)
  const uArmL = boxMesh(limbW * 0.95, uArmLen, limbW * 0.92, bodyMat)
  uArmL.position.y = -uArmLen / 2
  const armLL = new THREE.Group()
  armLL.position.y = -uArmLen
  const lArmLo = boxMesh(limbW * 0.88, lArmLen, limbW * 0.88, bodyMat)
  lArmLo.position.y = -lArmLen / 2
  armLL.add(lArmLo)
  armLU.add(uArmL, armLL)
  spine.add(armLU)

  const armRU = new THREE.Group()
  armRU.position.set(shoulderXOff, shoulderY, 0)
  const uArmR = boxMesh(limbW * 0.95, uArmLen, limbW * 0.92, bodyMat)
  uArmR.position.y = -uArmLen / 2
  const armRL = new THREE.Group()
  armRL.position.y = -uArmLen
  const rArmLo = boxMesh(limbW * 0.88, lArmLen, limbW * 0.88, bodyMat)
  rArmLo.position.y = -lArmLen / 2
  armRL.add(rArmLo)
  armRU.add(uArmR, armRL)
  spine.add(armRU)

  // 画布拖拽调关节：上臂网格同时影响肩 Z / 肩 X；肩组仅作备用（射线多击中网格）
  uArmL.userData.limbDrag = { shoulderL: true, mulZ: 0.22, mulX: 0.2, minZ: -115, maxZ: 115, minX: -85, maxX: 85 }
  uArmR.userData.limbDrag = { shoulderR: true, mulZ: 0.22, mulX: 0.2, minZ: -115, maxZ: 115, minX: -85, maxX: 85 }
  lArmLo.userData.limbDrag = { key: 'armLElbowX', useDX: false, mul: 0.34, min: 0, max: 168 }
  rArmLo.userData.limbDrag = { key: 'armRElbowX', useDX: false, mul: 0.34, min: 0, max: 168 }
  lHip.userData.limbDrag = { key: 'legLX', useDX: false, mul: 0.22, min: -75, max: 75 }
  lKnee.userData.limbDrag = { key: 'legLKneeX', useDX: false, mul: 0.3, min: 0, max: 168 }
  rHip.userData.limbDrag = { key: 'legRX', useDX: false, mul: 0.22, min: -75, max: 75 }
  rKnee.userData.limbDrag = { key: 'legRKneeX', useDX: false, mul: 0.3, min: 0, max: 168 }

  rig.add(lHip, rHip, pelvis, spine)

  const rigRefs = {
    lHip,
    lKnee,
    rHip,
    rKnee,
    spine,
    neck,
    armLU,
    armLL,
    armRU,
    armRL
  }
  setRigPose(rigRefs, pose)

  return { group, bodyMat, headMat, height, rig: rigRefs, pose }
}

function makeCube(id) {
  const group = new THREE.Group()
  group.userData.entityId = id
  group.userData.pickable = true
  group.rotation.order = 'YXZ'
  const sx = 0.55 + Math.random() * 0.25
  const sy = 0.45 + Math.random() * 0.2
  const sz = 0.55 + Math.random() * 0.25
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(sx, sy, sz),
    new THREE.MeshStandardMaterial({ color: 0xa16207, roughness: 0.72, metalness: 0.1 })
  )
  mesh.position.y = sy / 2
  mesh.castShadow = true
  mesh.userData.pickable = true
  group.add(mesh)
  return { group, mesh, sx, sy, sz }
}

function shotAspectRatio(str) {
  const m = {
    '16:9': 16 / 9,
    '4:3': 4 / 3,
    '9:16': 9 / 16,
    '1:1': 1
  }
  return m[str] || 16 / 9
}

export default {
  name: 'DirectorDeck3D',
  data() {
    return {
      showGrid: true,
      showAxes: true,
      lockScene: false,
      entityPanel: [],
      selectedId: null,
      edit: {
        type: 'character',
        x: 0,
        y: 0,
        z: 0,
        yawDeg: 0,
        pitchDeg: 0,
        height: DEF_CHAR_H,
        billboard: false,
        label: '',
        bodyColor: '#2563eb',
        pose: defaultMannequinPose()
      },
      shotEdit: { px: 3, py: 1.65, pz: 4.2, tx: 0, ty: 0.85, tz: 0, fov: 42 },
      shotAspect: '16:9',
      keyframes: [
        { time: 0, pos: [3, 1.65, 4.2], target: [0, 0.85, 0], fov: 42 },
        { time: 4, pos: [-2.2, 2.1, 5.4], target: [0, 0.9, 0], fov: 38 }
      ],
      duration: 5,
      playTime: 0,
      playing: false,
      hud: { pos: '', tgt: '', fov: '', frame: 0, warn: '' },
      previewW: 260,
      previewMargin: 10,
      /** 与右侧 aside（约 300px + 边距）对齐，须与 renderShotPreview 的 x 计算一致 */
      previewRightReserve: 312,
      /** 与顶栏 HUD 错开，须与 renderShotPreview 的 y 换算一致 */
      previewTopOffset: 48,
      /** 当前导入的 360° 全景文件名（仅展示，不写入导出 JSON 图像数据） */
      panoramaFileName: '',
      /** 节点画布「保存返回」导出进行中，防止连点 */
      ngcSaveReturnBusy: false
    }
  },
  computed: {
    previewWrapStyle() {
      const h = Math.round(this.previewW / shotAspectRatio(this.shotAspect))
      return {
        width: this.previewW + 'px',
        height: h + 'px',
        right: this.previewMargin + this.previewRightReserve + 'px',
        top: this.previewMargin + this.previewTopOffset + 'px'
      }
    },
    /** 由节点画布导演台节点打开（带 ngc=1） */
    isOpenedFromNodeGraph() {
      const q = this.$route.query || {}
      return q.ngc === '1' || q.ngc === 1
    },
    /** 已解析出下游图片节点 id，可写入预览图并返回 */
    ngcHasDownstreamImage() {
      const q = this.$route.query || {}
      return this.isOpenedFromNodeGraph && String(q.img || '').trim().length > 0
    },
    /** 保存返回按钮悬停说明 */
    ngcSaveReturnTitle() {
      if (!this.isOpenedFromNodeGraph) return ''
      return this.ngcHasDownstreamImage
        ? '上传预览图到云端并写入下游图片节点，回到节点画布'
        : '上传预览图到云端并同步到导演台节点，回到节点画布'
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initThree()
    })
    this._boundMove = e => this.onPointerMove(e)
    this._boundUp = e => this.onPointerUp(e)
    this._boundKey = e => this.onKey(e)
    window.addEventListener('pointermove', this._boundMove)
    window.addEventListener('pointerup', this._boundUp)
    window.addEventListener('pointercancel', this._boundUp)
    window.addEventListener('keydown', this._boundKey)
  },
  beforeDestroy() {
    window.removeEventListener('pointermove', this._boundMove)
    window.removeEventListener('pointerup', this._boundUp)
    window.removeEventListener('pointercancel', this._boundUp)
    window.removeEventListener('keydown', this._boundKey)
    this.teardownThree()
  },
  methods: {
    syncHelpers() {
      if (!this._scene) return
      if (this._grid) {
        this._scene.remove(this._grid)
        this._grid.geometry.dispose()
        this._grid.material.dispose()
        this._grid = null
      }
      if (this.showGrid) {
        this._grid = new THREE.GridHelper(24, 48, 0x334155, 0x1e293b)
        this._scene.add(this._grid)
      }
      if (this._axes) {
        this._scene.remove(this._axes)
        if (this._axes.geometry) this._axes.geometry.dispose()
        if (this._axes.material) this._axes.material.dispose()
        this._axes = null
      }
      if (this.showAxes) {
        this._axes = new THREE.AxesHelper(1.6)
        this._scene.add(this._axes)
      }
      this.syncPanoramaGroundVisibility()
    },
    /** 全景作为背景时隐藏不透明地板与网格，否则会挡住下半球画面 */
    syncPanoramaGroundVisibility() {
      const hasPano = !!this.panoramaFileName
      if (this._floor) this._floor.visible = !hasPano
      if (this._grid) this._grid.visible = hasPano ? false : this.showGrid
    },
    initThree() {
      const canvas = this.$refs.canvasEl
      if (!canvas) return
      if (this._renderer) return

      this._entities = []
      this._drag = null
      this._limbDrag = null
      this._raf = 0
      this._editorFovBeforePanorama = null
      this._toneExposureBeforePanorama = null

      const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false })
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
      renderer.setClearColor(0x07070d, 1)
      renderer.shadowMap.enabled = true
      renderer.shadowMap.type = THREE.PCFSoftShadowMap
      renderer.outputColorSpace = THREE.SRGBColorSpace
      renderer.toneMapping = THREE.ACESFilmicToneMapping
      renderer.toneMappingExposure = 1.02

      this._pmrem = new THREE.PMREMGenerator(renderer)
      this._pmrem.compileEquirectangularShader()
      this._panoramaTexture = null
      this._panoramaEnvRT = null

      const scene = new THREE.Scene()
      scene.fog = new THREE.FogExp2(0x0a0a14, 0.022)

      const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(80, 80),
        new THREE.MeshStandardMaterial({ color: 0x111118, roughness: 0.95, metalness: 0.02 })
      )
      floor.rotation.x = -Math.PI / 2
      floor.receiveShadow = true
      scene.add(floor)
      this._floor = floor

      const hemi = new THREE.HemisphereLight(0x8899cc, 0x080810, 0.45)
      scene.add(hemi)
      const dir = new THREE.DirectionalLight(0xfff5e6, 0.95)
      dir.position.set(-6, 10, 8)
      dir.castShadow = true
      dir.shadow.mapSize.set(2048, 2048)
      dir.shadow.camera.near = 0.5
      dir.shadow.camera.far = 60
      dir.shadow.camera.left = -14
      dir.shadow.camera.right = 14
      dir.shadow.camera.top = 14
      dir.shadow.camera.bottom = -14
      scene.add(dir)

      const editorCamera = new THREE.PerspectiveCamera(DEFAULT_EDITOR_FOV, 1, 0.08, 200)
      editorCamera.position.set(6.5, 3.4, 8.2)
      const controls = new OrbitControls(editorCamera, canvas)
      controls.target.set(0, 0.4, 0)
      controls.enableDamping = true
      controls.dampingFactor = 0.06
      controls.maxPolarAngle = Math.PI * 0.49

      const shotCamera = new THREE.PerspectiveCamera(
        this.shotEdit.fov,
        shotAspectRatio(this.shotAspect),
        0.12,
        120
      )
      shotCamera.position.set(this.shotEdit.px, this.shotEdit.py, this.shotEdit.pz)
      shotCamera.lookAt(this.shotEdit.tx, this.shotEdit.ty, this.shotEdit.tz)
      // 逻辑机位不加入场景图，仅用于导出/角贴渲染，避免出现 CameraHelper、锥体等

      this._renderer = renderer
      this._scene = scene
      this._editorCamera = editorCamera
      this._controls = controls
      this._shotCamera = shotCamera
      this._exportRT = null
      this._shotHelper = null
      this._cone = null
      this._raycaster = new THREE.Raycaster()
      this._pointerNDC = new THREE.Vector2()
      this._clock = new THREE.Clock()
      this.syncHelpers()
      this.rebuildPath()

      const setSize = () => {
        const w = canvas.clientWidth || 2
        const h = canvas.clientHeight || 2
        editorCamera.aspect = w / h
        editorCamera.updateProjectionMatrix()
        renderer.setSize(w, h, false)
      }
      setSize()
      this.syncShotFromEditor()
      this._onWinResize = setSize
      window.addEventListener('resize', setSize)
      this._resizeObs = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(setSize) : null
      if (this._resizeObs) this._resizeObs.observe(canvas.parentElement || canvas)

      const tick = () => {
        this._raf = requestAnimationFrame(tick)
        const dt = this._clock ? Math.min(0.05, this._clock.getDelta()) : 0.016
        if (this.playing) {
          this.playTime += dt
          if (this.playTime >= this.duration) this.playTime = 0
          this.applyShotFromKeyframes(this.playTime)
          this.applyShotToEditorCamera()
          if (this._controls) this._controls.enabled = false
        } else {
          if (this._controls) this._controls.enabled = !this.lockScene
          controls.update()
          this.syncShotFromEditor()
        }
        this.updateBillboards()
        if (this._ngcBackdropVideoTex && this._ngcBackdropVideo) {
          try {
            if (this._ngcBackdropVideo.readyState >= 2) this._ngcBackdropVideoTex.needsUpdate = true
          } catch {
            /* ignore */
          }
        }
        this.updateHud()
        renderer.setViewport(0, 0, canvas.clientWidth, canvas.clientHeight)
        renderer.setScissorTest(false)
        renderer.render(scene, editorCamera)
        this.renderShotPreview()
      }
      tick()
      this.$nextTick(() => {
        try {
          this.tryImportNodeGraphUpstreamMedia()
        } catch {
          /* ignore */
        }
      })
    },
    renderShotPreview() {
      const r = this._renderer
      const c = this.$refs.canvasEl
      if (!r || !c || !this._shotCamera || !this._scene) return
      const pw = this.previewW
      const ph = Math.round(pw / shotAspectRatio(this.shotAspect))
      const margin = this.previewMargin
      const reserve = this.previewRightReserve
      const topCss = margin + this.previewTopOffset
      const x = Math.max(0, c.clientWidth - pw - margin - reserve)
      // WebGL viewport/scissor 的 y 为画布左下角为原点，须与 CSS top 对齐
      const y = Math.max(0, c.clientHeight - topCss - ph)
      this._shotCamera.aspect = pw / ph
      this._shotCamera.updateProjectionMatrix()
      r.setScissorTest(true)
      r.setViewport(x, y, pw, ph)
      r.setScissor(x, y, pw, ph)
      r.setClearColor(0x020204, 1)
      r.clear(true, true, true)
      r.render(this._scene, this._shotCamera)
      r.setScissorTest(false)
      r.setViewport(0, 0, c.clientWidth, c.clientHeight)
      r.setClearColor(0x07070d, 1)
    },
    updateBillboards() {
      if (!this._editorCamera || !this._entities) return
      this._entities.forEach(ent => {
        if (ent.type !== 'character' || !ent.billboard) return
        const p = ent.group.position
        const c = this._editorCamera.position
        const pitch = ent.group.rotation.x
        ent.group.rotation.order = 'YXZ'
        ent.group.rotation.y = Math.atan2(c.x - p.x, c.z - p.z)
        ent.group.rotation.x = pitch
        ent.group.rotation.z = 0
      })
    },
    applyShotFromKeyframes(t) {
      const k = interpolateShotKeyframes(this.keyframes, t, { ease: true })
      if (!k || !this._shotCamera) return
      this._shotCamera.position.set(k.pos[0], k.pos[1], k.pos[2])
      this._shotCamera.lookAt(k.target[0], k.target[1], k.target[2])
      this._shotCamera.fov = k.fov
      this._shotCamera.updateProjectionMatrix()
      this.shotEdit.px = k.pos[0]
      this.shotEdit.py = k.pos[1]
      this.shotEdit.pz = k.pos[2]
      this.shotEdit.tx = k.target[0]
      this.shotEdit.ty = k.target[1]
      this.shotEdit.tz = k.target[2]
      this.shotEdit.fov = k.fov
    },
    updateHud() {
      const sc = this._shotCamera
      if (!sc) return
      this.hud.pos = `${sc.position.x.toFixed(2)}, ${sc.position.y.toFixed(2)}, ${sc.position.z.toFixed(2)}`
      const t = new THREE.Vector3(this.shotEdit.tx, this.shotEdit.ty, this.shotEdit.tz)
      this.hud.tgt = `${t.x.toFixed(2)}, ${t.y.toFixed(2)}, ${t.z.toFixed(2)}`
      this.hud.fov = sc.fov.toFixed(1)
      this.hud.frame = Math.round((this.playTime / Math.max(this.duration, 0.001)) * 240)
      let warn = ''
      if (this._entities) {
        this._entities.forEach(e => {
          const p = e.group.position
          if (Math.abs(p.x) > BOUNDS || Math.abs(p.z) > BOUNDS) warn = '有实体接近或超出场景边界'
        })
      }
      this.hud.warn = warn
    },
    rebuildPath() {
      if (this._scene && this._pathLine) {
        this._scene.remove(this._pathLine)
        this._pathLine.geometry.dispose()
        this._pathLine.material.dispose()
        this._pathLine = null
      }
      const sorted = [...this.keyframes].sort((a, b) => a.time - b.time)
      if (sorted.length < 2) {
        this.duration = sorted.length ? Math.max(...sorted.map(k => k.time), 0.1) + 0.5 : 0.1
        return
      }
      this.duration = Math.max(...sorted.map(k => k.time), 0.1) + 0.5
    },
    syncEntityPanel() {
      this.entityPanel = (this._entities || []).map(e => {
        const r = readEntityYawPitchDeg(e.group)
        const bodyHex =
          e.type === 'character'
            ? e.bodyColorHex != null
              ? e.bodyColorHex
              : e.bodyMat && e.bodyMat.color
                ? e.bodyMat.color.getHex()
                : 0x2563eb
            : null
        return {
          id: e.id,
          type: e.type,
          label: e.label || (e.type === 'character' ? '假人' : '立方体'),
          x: e.group.position.x,
          y: e.group.position.y,
          z: e.group.position.z,
          yawDeg: r.yawDeg,
          pitchDeg: r.pitchDeg,
          height: e.height || DEF_CHAR_H,
          billboard: !!e.billboard,
          ...(e.type === 'character' ? { bodyColorCss: bodyColorCssFromHex(bodyHex) } : {})
        }
      })
    },
    genId() {
      return `e_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
    },
    placementRay() {
      if (!this._raycaster || !this._editorCamera || !this._pointerNDC) return { x: 0, z: 0 }
      const c = this.$refs.canvasEl
      const cam = this._editorCamera
      if (!c || !cam) return { x: 0, z: 0 }
      this._pointerNDC.set(0, 0.06)
      this._raycaster.setFromCamera(this._pointerNDC, cam)
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
      const hit = new THREE.Vector3()
      this._raycaster.ray.intersectPlane(plane, hit)
      return { x: Math.max(-BOUNDS + 0.5, Math.min(BOUNDS - 0.5, hit.x)), z: Math.max(-BOUNDS + 0.5, Math.min(BOUNDS - 0.5, hit.z)) }
    },
    addCharacter() {
      if (!this._scene || !this._raycaster || !this._editorCamera) return
      if (!this._entities) this._entities = []
      const id = this.genId()
      const defaultHex = 0x2563eb
      const { group, bodyMat, headMat, rig, pose } = makeCharacter(id, DEF_CHAR_H, defaultHex)
      const p = this.placementRay()
      group.position.set(p.x, 0, p.z)
      this._scene.add(group)
      this._entities.push({
        id,
        type: 'character',
        group,
        height: DEF_CHAR_H,
        billboard: false,
        bodyColorHex: defaultHex,
        bodyMat,
        headMat,
        rig,
        pose: { ...pose },
        label: `假人 ${this._entities.filter(x => x.type === 'character').length + 1}`
      })
      this.syncEntityPanel()
      this.selectEntity(id)
    },
    addCube() {
      if (!this._scene || !this._raycaster || !this._editorCamera) return
      if (!this._entities) this._entities = []
      const id = this.genId()
      const { group, sx, sy, sz } = makeCube(id)
      const p = this.placementRay()
      group.position.set(p.x + 0.4, 0, p.z + 0.4)
      this._scene.add(group)
      this._entities.push({ id, type: 'cube', group, sx, sy, sz, label: `物体 ${this._entities.filter(x => x.type === 'cube').length + 1}` })
      this.syncEntityPanel()
      this.selectEntity(id)
    },
    selectEntity(id) {
      this.selectedId = id
      if (!this._entities) return
      this._entities.forEach(e => {
        const mats = []
        if (e.type === 'character') {
          if (e.bodyMat) mats.push(e.bodyMat)
          if (e.headMat) mats.push(e.headMat)
        } else if (e.type === 'cube' && e.mesh) {
          mats.push(e.mesh.material)
        }
        mats.forEach(em => {
          if (em && em.emissive) {
            em.emissive.setHex(e.id === id ? 0x4c1d95 : 0x000000)
            em.emissiveIntensity = e.id === id ? 0.35 : 0
          }
        })
      })
      const ent = this._entities.find(x => x.id === id)
      if (!ent) return
      this.edit.type = ent.type
      this.edit.x = ent.group.position.x
      this.edit.y = ent.group.position.y
      this.edit.z = ent.group.position.z
      const r = readEntityYawPitchDeg(ent.group)
      this.edit.yawDeg = r.yawDeg
      this.edit.pitchDeg = r.pitchDeg
      this.edit.height = ent.height || DEF_CHAR_H
      this.edit.billboard = !!ent.billboard
      if (ent.type === 'character' && ent.pose) {
        Object.assign(this.edit.pose, defaultMannequinPose(), ent.pose)
        this.edit.label = ent.label != null ? String(ent.label) : ''
        const bh =
          ent.bodyColorHex != null
            ? ent.bodyColorHex
            : ent.bodyMat && ent.bodyMat.color
              ? ent.bodyMat.color.getHex()
              : 0x2563eb
        this.edit.bodyColor = bodyColorCssFromHex(bh)
      } else {
        this.edit.label = ''
        this.edit.bodyColor = '#2563eb'
      }
    },
    removeSelected() {
      const id = this.selectedId
      if (!id || !this._scene || !this._entities) return
      const i = this._entities.findIndex(x => x.id === id)
      if (i < 0) return
      const ent = this._entities[i]
      this._scene.remove(ent.group)
      disposeObject3D(ent.group)
      this._entities.splice(i, 1)
      this.selectedId = null
      this.syncEntityPanel()
    },
    applyEditTransform() {
      const ent = this._entities.find(x => x.id === this.selectedId)
      if (!ent) return
      ent.group.position.set(this.edit.x, this.edit.y, this.edit.z)
      writeEntityYawPitchDeg(ent.group, this.edit.yawDeg, this.edit.pitchDeg)
      ent.billboard = !!this.edit.billboard
      this.syncEntityPanel()
    },
    applyCharacterHeight() {
      const ent = this._entities.find(x => x.id === this.selectedId && x.type === 'character')
      if (!ent || !ent.group) return
      const h = Math.min(2.4, Math.max(0.7, Number(this.edit.height) || DEF_CHAR_H))
      let pose = { ...defaultMannequinPose(), ...(ent.pose || {}) }
      if (this.selectedId === ent.id && this.edit.pose) {
        Object.assign(pose, this.edit.pose)
      }
      ent.height = h
      while (ent.group.children.length) {
        const ch = ent.group.children[0]
        ent.group.remove(ch)
        disposeObject3D(ch)
      }
      const col =
        ent.bodyColorHex != null
          ? ent.bodyColorHex
          : ent.bodyMat && ent.bodyMat.color
            ? ent.bodyMat.color.getHex()
            : 0x2563eb
      const built = makeCharacter(ent.id, h, col, pose)
      ent.bodyColorHex = col
      ent.bodyMat = built.bodyMat
      ent.headMat = built.headMat
      ent.rig = built.rig
      ent.pose = { ...built.pose }
      built.group.children.slice().forEach(ch => ent.group.add(ch))
      if (this.selectedId === ent.id) {
        Object.assign(this.edit.pose, ent.pose)
      }
      this.syncEntityPanel()
      if (this.selectedId === ent.id) this.selectEntity(ent.id)
    },
    applyMannequinPose() {
      const ent = this._entities.find(x => x.id === this.selectedId && x.type === 'character')
      if (!ent || !ent.rig) return
      Object.assign(ent.pose, this.edit.pose)
      setRigPose(ent.rig, ent.pose)
    },
    resetMannequinPose() {
      if (this.lockScene) return
      const ent = this._entities.find(x => x.id === this.selectedId && x.type === 'character')
      if (!ent || !ent.rig) return
      const fresh = defaultMannequinPose()
      Object.assign(this.edit.pose, fresh)
      Object.assign(ent.pose, fresh)
      setRigPose(ent.rig, ent.pose)
    },
    applyCharacterLabel() {
      const ent = this._entities.find(x => x.id === this.selectedId && x.type === 'character')
      if (!ent) return
      const raw = (this.edit.label || '').trim()
      const idx = this._entities.filter(x => x.type === 'character').findIndex(x => x.id === ent.id)
      ent.label = raw || `假人 ${idx >= 0 ? idx + 1 : 1}`
      this.syncEntityPanel()
    },
    applyCharacterBodyColor() {
      const ent = this._entities.find(x => x.id === this.selectedId && x.type === 'character')
      if (!ent || !ent.bodyMat) return
      const hex = parseBodyColorInput(this.edit.bodyColor)
      if (hex == null) return
      ent.bodyColorHex = hex
      ent.bodyMat.color.setHex(hex)
      this.syncEntityPanel()
    },
    alignFeet() {
      const ent = this._entities.find(x => x.id === this.selectedId)
      if (!ent) return
      ent.group.position.y = 0
      this.edit.y = 0
      this.syncEntityPanel()
    },
    faceShotCamera() {
      const ent = this._entities.find(x => x.id === this.selectedId)
      if (!ent || !this._editorCamera) return
      const p = ent.group.position
      const c = this._editorCamera.position
      const r = readEntityYawPitchDeg(ent.group)
      const yawDeg = (Math.atan2(c.x - p.x, c.z - p.z) * 180) / Math.PI
      writeEntityYawPitchDeg(ent.group, yawDeg, r.pitchDeg)
      this.edit.yawDeg = yawDeg
      this.edit.pitchDeg = r.pitchDeg
      this.syncEntityPanel()
    },
    /** 主画布 Orbit 相机 → 导出用逻辑机位（与画面一致） */
    syncShotFromEditor() {
      const ec = this._editorCamera
      const sc = this._shotCamera
      const controls = this._controls
      if (!ec || !sc || !controls) return
      sc.position.copy(ec.position)
      sc.quaternion.copy(ec.quaternion)
      sc.fov = ec.fov
      sc.near = ec.near
      sc.far = ec.far
      sc.updateProjectionMatrix()
      this.shotEdit.px = ec.position.x
      this.shotEdit.py = ec.position.y
      this.shotEdit.pz = ec.position.z
      this.shotEdit.tx = controls.target.x
      this.shotEdit.ty = controls.target.y
      this.shotEdit.tz = controls.target.z
      this.shotEdit.fov = ec.fov
    },
    /** 关键帧插值结果 → 主视图相机（播放运镜） */
    applyShotToEditorCamera() {
      const ec = this._editorCamera
      const sc = this._shotCamera
      const controls = this._controls
      const canvas = this.$refs.canvasEl
      if (!ec || !sc || !controls) return
      ec.position.copy(sc.position)
      ec.quaternion.copy(sc.quaternion)
      ec.fov = sc.fov
      if (canvas && canvas.clientHeight > 0) {
        ec.aspect = (canvas.clientWidth || 2) / (canvas.clientHeight || 2)
      }
      ec.updateProjectionMatrix()
      controls.target.set(this.shotEdit.tx, this.shotEdit.ty, this.shotEdit.tz)
      controls.update()
    },
    applyExportFovToEditor() {
      if (!this._editorCamera) return
      const f = Math.min(100, Math.max(10, Number(this.shotEdit.fov) || 48))
      this.shotEdit.fov = f
      this._editorCamera.fov = f
      this._editorCamera.updateProjectionMatrix()
    },
    onShotAspectChange() {
      if (this._shotCamera) {
        this._shotCamera.aspect = shotAspectRatio(this.shotAspect)
        this._shotCamera.updateProjectionMatrix()
      }
    },
    addKeyframe() {
      this.keyframes.push({
        time: this.duration * 0.85,
        pos: [this.shotEdit.px, this.shotEdit.py, this.shotEdit.pz],
        target: [this.shotEdit.tx, this.shotEdit.ty, this.shotEdit.tz],
        fov: this.shotEdit.fov
      })
      this.rebuildPath()
    },
    removeKeyframe(idx) {
      if (this.keyframes.length <= 2) return
      this.keyframes.splice(idx, 1)
      this.rebuildPath()
    },
    syncKeyframesFromShot() {
      if (!this.keyframes.length) return
      const sorted = [...this.keyframes].sort((a, b) => a.time - b.time)
      let nearest = sorted[0]
      let best = Math.abs(nearest.time - this.playTime)
      sorted.forEach(k => {
        const d = Math.abs(k.time - this.playTime)
        if (d < best) {
          best = d
          nearest = k
        }
      })
      nearest.pos = [this.shotEdit.px, this.shotEdit.py, this.shotEdit.pz]
      nearest.target = [this.shotEdit.tx, this.shotEdit.ty, this.shotEdit.tz]
      nearest.fov = this.shotEdit.fov
      this.rebuildPath()
    },
    onScrub() {
      if (this.playing) return
      this.applyShotFromKeyframes(this.playTime)
      this.applyShotToEditorCamera()
    },
    togglePlay() {
      this.playing = !this.playing
      if (this.playing && this._clock) this._clock.start()
      if (this._controls) this._controls.enabled = !this.lockScene && !this.playing
    },
    toggleLock() {
      this.lockScene = !this.lockScene
      if (this._controls) this._controls.enabled = !this.lockScene && !this.playing
    },
    openPanoramaPicker() {
      const el = this.$refs.panoramaFileInput
      if (el) el.click()
    },
    onPanoramaFileChange(ev) {
      const input = ev.target
      const file = input.files && input.files[0]
      if (input) input.value = ''
      if (!file || this.lockScene) return
      this.loadPanoramaFromFile(file)
    },
    loadPanoramaFromFile(file) {
      if (!file || !this._renderer || !this._scene || !this._pmrem) return
      const ext = (file.name.split('.').pop() || '').toLowerCase()
      const allowed = ['jpg', 'jpeg', 'png', 'webp', 'hdr']
      if (!allowed.includes(ext)) {
        window.alert('请选择 JPG / PNG / WebP 或 HDR（360° 等距柱状投影，宽高比约 2:1）。')
        return
      }
      const url = URL.createObjectURL(file)
      const isHdr = ext === 'hdr'
      const revoke = () => {
        try {
          URL.revokeObjectURL(url)
        } catch (e) {
          /* ignore */
        }
      }
      const apply = tex => {
        revoke()
        this.applyPanoramaTexture(tex, file.name, isHdr)
      }
      const fail = err => {
        revoke()
        console.error(err)
        window.alert('全景图加载失败，请确认文件为有效的 360° 等距柱状图。')
      }
      if (isHdr) {
        new RGBELoader().load(url, apply, undefined, fail)
      } else {
        new THREE.TextureLoader().load(url, apply, undefined, fail)
      }
    },
    applyPanoramaTexture(texture, fileName, isHdr) {
      if (!this._scene || !this._renderer || !this._pmrem) {
        texture.dispose()
        return
      }
      this.disposePanoramaMaps(true)
      // LDR 网页贴图默认 flipY=true，与 WebGL/等距柱状背景一致；设为 false 时常见整幅上下颠倒
      texture.flipY = true
      texture.mapping = THREE.EquirectangularReflectionMapping
      texture.colorSpace = isHdr ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace
      if (!isHdr) {
        texture.generateMipmaps = true
        texture.minFilter = THREE.LinearMipmapLinearFilter
        texture.magFilter = THREE.LinearFilter
      }
      texture.needsUpdate = true
      const cap = this._renderer.capabilities.getMaxAnisotropy()
      texture.anisotropy = Math.min(8, cap || 1)
      const rt = this._pmrem.fromEquirectangular(texture)
      this._panoramaTexture = texture
      this._panoramaEnvRT = rt
      this._scene.background = texture
      this._scene.environment = rt.texture
      this._scene.fog = null
      if (this._editorCamera && this._editorFovBeforePanorama == null) {
        this._editorFovBeforePanorama = this._editorCamera.fov
      }
      if (this._editorCamera) {
        this._editorCamera.fov = PANORAMA_EDITOR_FOV
        this._editorCamera.updateProjectionMatrix()
      }
      if (this._renderer && this._toneExposureBeforePanorama == null) {
        this._toneExposureBeforePanorama = this._renderer.toneMappingExposure
      }
      if (this._renderer && isHdr) {
        this._renderer.toneMappingExposure = Math.min(0.96, this._toneExposureBeforePanorama * 0.9)
      }
      this.panoramaFileName = fileName || '全景图'
      this.syncPanoramaGroundVisibility()
    },
    disposePanoramaMaps(skipGroundSync) {
      if (this._ngcBackdropVideoTex) {
        try {
          this._ngcBackdropVideoTex.dispose()
        } catch {
          /* ignore */
        }
        this._ngcBackdropVideoTex = null
      }
      if (this._ngcBackdropVideo) {
        try {
          this._ngcBackdropVideo.pause()
          this._ngcBackdropVideo.removeAttribute('src')
          this._ngcBackdropVideo.load()
        } catch {
          /* ignore */
        }
        this._ngcBackdropVideo = null
      }
      if (this._editorCamera != null && this._editorFovBeforePanorama != null) {
        this._editorCamera.fov = this._editorFovBeforePanorama
        this._editorCamera.updateProjectionMatrix()
        this._editorFovBeforePanorama = null
      }
      if (this._renderer != null && this._toneExposureBeforePanorama != null) {
        this._renderer.toneMappingExposure = this._toneExposureBeforePanorama
        this._toneExposureBeforePanorama = null
      }
      if (this._panoramaEnvRT) {
        this._panoramaEnvRT.dispose()
        this._panoramaEnvRT = null
      }
      if (this._panoramaTexture) {
        this._panoramaTexture.dispose()
        this._panoramaTexture = null
      }
      if (this._scene) {
        this._scene.background = null
        this._scene.environment = null
        this._scene.fog = new THREE.FogExp2(0x0a0a14, 0.022)
      }
      this.panoramaFileName = ''
      if (!skipGroundSync) this.syncPanoramaGroundVisibility()
    },
    clearPanorama() {
      if (this.lockScene) return
      this.disposePanoramaMaps()
    },
    /** 从节点画布进入：自动将上游图片/视频载入为场景背景（图片按 360° 全景流程；视频为平面动态背景） */
    tryImportNodeGraphUpstreamMedia() {
      const q = this.$route.query || {}
      if (!(q.ngc === '1' || q.ngc === 1)) return
      const deck = String(q.deck || '').trim()
      if (!deck || !this._renderer || !this._scene || !this._pmrem) return
      let raw = ''
      try {
        raw = sessionStorage.getItem(NGC_DIRECTOR_UPSTREAM_V1) || ''
      } catch {
        return
      }
      if (!raw) return
      let obj = null
      try {
        obj = JSON.parse(raw)
      } catch {
        return
      }
      if (!obj || obj.deckId !== deck || !Array.isArray(obj.items)) return
      const firstImg = obj.items.find(x => x && x.kind === 'image' && String(x.url || '').trim())
      const firstVid = obj.items.find(x => x && x.kind === 'video' && String(x.url || '').trim())
      if (firstImg) {
        this.loadPanoramaFromImageUrl(String(firstImg.url).trim(), firstImg.title || '节点图片')
        return
      }
      if (firstVid) {
        this.loadPanoramaFromVideoUrl(String(firstVid.url).trim(), firstVid.title || '节点视频')
      }
    },
    loadPanoramaFromImageUrl(url, label) {
      if (!url || !this._renderer || !this._scene || !this._pmrem) return
      new THREE.TextureLoader().load(
        url,
        tex => {
          try {
            this.applyPanoramaTexture(tex, label || '节点图片', false)
            showSuccessToast('已载入上游图片为背景')
          } catch {
            try {
              tex.dispose()
            } catch {
              /* ignore */
            }
            showErrorToast('上游图片应用失败')
          }
        },
        undefined,
        () => {
          showErrorToast('上游图片加载失败（可检查跨域或链接是否有效）')
        }
      )
    },
    loadPanoramaFromVideoUrl(url, label) {
      if (!url || !this._scene || !this._renderer) return
      this.disposePanoramaMaps(true)
      const video = document.createElement('video')
      video.crossOrigin = 'anonymous'
      video.muted = true
      video.loop = true
      video.playsInline = true
      video.setAttribute('playsinline', '')
      video.src = url
      const fail = () => {
        showErrorToast('上游视频加载失败（格式或跨域）')
        try {
          video.pause()
          video.removeAttribute('src')
        } catch {
          /* ignore */
        }
      }
      video.addEventListener('error', fail, { once: true })
      video.addEventListener(
        'loadeddata',
        () => {
          video.play().catch(() => {})
          try {
            const tex = new THREE.VideoTexture(video)
            tex.colorSpace = THREE.SRGBColorSpace
            tex.minFilter = THREE.LinearFilter
            tex.magFilter = THREE.LinearFilter
            if (this._scene) {
              this._scene.background = tex
              this._scene.environment = null
              this._scene.fog = null
            }
            this._ngcBackdropVideo = video
            this._ngcBackdropVideoTex = tex
            this.panoramaFileName = label || '节点视频'
            this.syncPanoramaGroundVisibility()
            showSuccessToast('已载入上游视频为背景')
          } catch (e) {
            console.error(e)
            fail()
          }
        },
        { once: true }
      )
      video.load()
    },
    /** 从节点画布：导出后经 sessionStorage 回写导演台/下游图片节点，不触发浏览器本地下载 */
    saveAndReturnFromNodeCanvas() {
      if (this.ngcSaveReturnBusy) return
      this.ngcSaveReturnBusy = true
      this.exportPreviewPng({
        skipDownload: true,
        onComplete: () => {
          this.ngcSaveReturnBusy = false
        }
      })
    },
    goBackFromDirector() {
      const q = this.$route.query || {}
      if (q.ngc === '1' || q.ngc === 1) {
        const wq = { node: '1' }
        const canvasRaw = q.canvas
        const canvas = canvasRaw != null ? (Array.isArray(canvasRaw) ? canvasRaw[0] : String(canvasRaw)) : ''
        if (String(canvas || '').trim()) wq.canvas = String(canvas).trim()
        const img = String(q.img || '').trim()
        const deck = String(q.deck || '').trim()
        if (img) wq.focusNode = img
        else if (deck) wq.focusNode = deck
        if (wq.focusNode) wq.ngcFocus = '1'
        this.$router.push({ path: '/workspace', query: wq }).catch(() => {})
        return
      }
      this.$router.push('/home').catch(() => {})
    },
    /** 导出 PNG 后：先上传 /video_images 得云端 URL 再回画布；失败则回退 dataUrl（v2） */
    maybeNgcNavigateAfterExportPng(blob, onDone) {
      const done = typeof onDone === 'function' ? onDone : null
      const q = this.$route.query || {}
      if (!(q.ngc === '1' || q.ngc === 1)) {
        if (done) done()
        return
      }
      const deck = String(q.deck || '').trim()
      const img = String(q.img || '').trim()
      const canvasRaw = q.canvas
      const canvas = canvasRaw != null ? (Array.isArray(canvasRaw) ? canvasRaw[0] : String(canvasRaw)) : ''
      if (!deck && !img) {
        if (done) done()
        return
      }

      const wq = { node: '1', ngcDirectorShot: '1', focusNode: img || deck || '' }
      if (String(canvas || '').trim()) wq.canvas = String(canvas).trim()

      const finishNavigate = () => {
        if (String(canvas || '').trim()) {
          try {
            sessionStorage.setItem(NGC_LAST_OPENED_CANVAS_KEY, String(canvas).trim())
          } catch {
            /* ignore */
          }
        }
        setTimeout(() => {
          this.$router.push({ path: '/workspace', query: wq }).catch(() => {})
          if (done) done()
        }, 200)
      }

      const tryStoreV2FromReader = () => {
        const reader = new FileReader()
        reader.onload = () => {
          const dataUrl = reader.result
          const payload = JSON.stringify({
            v: 2,
            dataUrl,
            imageNodeId: img || '',
            deckId: deck || ''
          })
          try {
            sessionStorage.setItem(NGC_DIRECTOR_PNG_SESSION_KEY, payload)
          } catch {
            try {
              sessionStorage.removeItem(NGC_DIRECTOR_PNG_SESSION_KEY)
            } catch {
              /* ignore */
            }
            try {
              const url = URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `director-preview-fallback-${Date.now()}.png`
              a.rel = 'noopener'
              document.body.appendChild(a)
              a.click()
              document.body.removeChild(a)
              URL.revokeObjectURL(url)
            } catch {
              /* ignore */
            }
            delete wq.ngcDirectorShot
            wq.ngcFocus = '1'
            showErrorToast('上传失败且预览过大无法缓存；已下载到本机，请手动拖入节点')
          }
          finishNavigate()
        }
        reader.onerror = () => {
          if (done) done()
        }
        reader.readAsDataURL(blob)
      }

      ;(async () => {
        try {
          const fn = `director-preview-${String(this.shotAspect || '16-9').replace(/:/g, '-')}-${Date.now()}.png`
          const imageUrl = await uploadVideoMediaFile({ file: blob, filename: fn, title: '导演台预览' })
          const u = imageUrl != null ? String(imageUrl).trim() : ''
          if (u && (/^https?:\/\//i.test(u) || u.startsWith('/'))) {
            try {
              sessionStorage.setItem(
                NGC_DIRECTOR_PNG_SESSION_KEY,
                JSON.stringify({
                  v: 3,
                  imageUrl: u,
                  imageNodeId: img || '',
                  deckId: deck || ''
                })
              )
            } catch {
              tryStoreV2FromReader()
              return
            }
            finishNavigate()
            return
          }
        } catch (e) {
          console.warn('[DirectorDeck] preview upload failed', e)
        }
        tryStoreV2FromReader()
      })()
    },
    exportJson() {
      const characters = (this._entities || [])
        .filter(e => e.type === 'character')
        .map(e => {
          const r = readEntityYawPitchDeg(e.group)
          const bodyHex =
            e.bodyColorHex != null
              ? e.bodyColorHex
              : e.bodyMat && e.bodyMat.color
                ? e.bodyMat.color.getHex()
                : 0x2563eb
          return {
            id: e.id,
            x: e.group.position.x,
            y: e.group.position.y,
            z: e.group.position.z,
            yawDeg: r.yawDeg,
            pitchDeg: r.pitchDeg,
            height: e.height,
            label: e.label,
            body_color: bodyColorCssFromHex(bodyHex),
            billboard: !!e.billboard,
            pose: e.pose ? { ...e.pose } : { ...defaultMannequinPose() }
          }
        })
      const sceneObjects = (this._entities || [])
        .filter(e => e.type === 'cube')
        .map(e => {
          const r = readEntityYawPitchDeg(e.group)
          return {
            id: e.id,
            type: 'box',
            x: e.group.position.x,
            y: e.group.position.y,
            z: e.group.position.z,
            yawDeg: r.yawDeg,
            pitchDeg: r.pitchDeg,
            sx: e.sx,
            sy: e.sy,
            sz: e.sz
          }
        })
      const payload = buildDirectorExportPayload({
        shotAspect: this.shotAspect,
        characters,
        sceneObjects,
        shotKeyframes: this.keyframes.map(k => ({
          time: k.time,
          pos: [...k.pos],
          target: [...k.target],
          fov: k.fov
        })),
        durationSec: this.duration,
        meta: {
          editor: 'DirectorDeck3D',
          exportedAt: new Date().toISOString(),
          ...(this.panoramaFileName ? { panoramaFileName: this.panoramaFileName } : {})
        }
      })
      downloadJson(payload, `director-scene-${Date.now()}.json`)
    },
    /** 与右上角「导出取景」同一机位与画幅，渲染到离屏目标后下载 PNG（长边 1920） */
    exportPreviewPng(rawOpts) {
      const opts =
        rawOpts &&
        typeof rawOpts === 'object' &&
        ('skipDownload' in rawOpts || typeof rawOpts.onComplete === 'function')
          ? rawOpts
          : {}
      const skipDownload = !!opts.skipDownload
      const onComplete = typeof opts.onComplete === 'function' ? opts.onComplete : null
      const finish = () => {
        if (onComplete) {
          try {
            onComplete()
          } catch {
            /* ignore */
          }
        }
      }

      const renderer = this._renderer
      const scene = this._scene
      const shot = this._shotCamera
      const canvas = this.$refs.canvasEl
      if (!renderer || !scene || !shot || !canvas) {
        finish()
        return
      }

      if (this.playing) this.applyShotFromKeyframes(this.playTime)
      else this.syncShotFromEditor()
      this.updateBillboards()

      const ar = shotAspectRatio(this.shotAspect)
      const long = 1920
      let expW
      let expH
      if (ar >= 1) {
        expW = long
        expH = Math.max(2, Math.round(long / ar))
      } else {
        expH = long
        expW = Math.max(2, Math.round(long * ar))
      }

      if (!this._exportRT || this._exportRT.width !== expW || this._exportRT.height !== expH) {
        if (this._exportRT) {
          this._exportRT.dispose()
          this._exportRT = null
        }
        this._exportRT = new THREE.WebGLRenderTarget(expW, expH, {
          depthBuffer: true,
          stencilBuffer: false,
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter
        })
        if (this._exportRT.texture && renderer.outputColorSpace) {
          this._exportRT.texture.colorSpace = renderer.outputColorSpace
        }
      }

      const prevAspect = shot.aspect
      shot.aspect = expW / expH
      shot.updateProjectionMatrix()

      const prevTarget = renderer.getRenderTarget()
      const prevClear = renderer.getClearColor(new THREE.Color())
      const prevAlpha = renderer.getClearAlpha()

      renderer.setRenderTarget(this._exportRT)
      renderer.setClearColor(0x020204, 1)
      renderer.clear(true, true, true)
      renderer.render(scene, shot)
      renderer.setRenderTarget(prevTarget)
      renderer.setClearColor(prevClear, prevAlpha)

      shot.aspect = prevAspect
      shot.updateProjectionMatrix()

      const w = this._exportRT.width
      const h = this._exportRT.height
      const buffer = new Uint8Array(w * h * 4)
      renderer.readRenderTargetPixels(this._exportRT, 0, 0, w, h, buffer)

      const row = w * 4
      const flipped = new Uint8Array(w * h * 4)
      for (let y = 0; y < h; y++) {
        flipped.set(buffer.subarray((h - 1 - y) * row, (h - y) * row), y * row)
      }

      const c2d = document.createElement('canvas')
      c2d.width = w
      c2d.height = h
      const ctx = c2d.getContext('2d')
      if (!ctx) {
        finish()
        return
      }
      const imgData = ctx.createImageData(w, h)
      imgData.data.set(flipped)
      ctx.putImageData(imgData, 0, 0)
      c2d.toBlob(blob => {
        if (!blob) {
          finish()
          return
        }
        if (!skipDownload) {
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `director-preview-${this.shotAspect.replace(':', '-')}-${w}x${h}-${Date.now()}.png`
          a.rel = 'noopener'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        }
        this.maybeNgcNavigateAfterExportPng(blob, onComplete ? finish : null)
      }, 'image/png')
    },
    updateRaycasterFromClient(clientX, clientY) {
      if (!this._raycaster || !this._editorCamera || !this._pointerNDC) return false
      const c = this.$refs.canvasEl
      if (!c) return false
      const rect = c.getBoundingClientRect()
      this._pointerNDC.x = ((clientX - rect.left) / rect.width) * 2 - 1
      this._pointerNDC.y = -((clientY - rect.top) / rect.height) * 2 + 1
      this._raycaster.setFromCamera(this._pointerNDC, this._editorCamera)
      return true
    },
    pickEntityHitDistance() {
      if (!this._scene || !this._raycaster) return { entity: null, dist: Infinity }
      const objs = []
      this._scene.traverse(o => {
        if (o.isMesh && o.userData.pickable) objs.push(o)
      })
      const hits = this._raycaster.intersectObjects(objs, true)
      for (const h of hits) {
        let o = h.object
        while (o && !o.userData.entityId) o = o.parent
        if (o && o.userData.entityId) {
          const ent = (this._entities || []).find(e => e.id === o.userData.entityId)
          if (ent) return { entity: ent, dist: h.distance }
        }
      }
      return { entity: null, dist: Infinity }
    },
    /** 整体平移拾取：忽略先命中到的肢体网格，避免拖假人时误移动全身 */
    pickEntityForPositionDrag() {
      if (!this._scene || !this._raycaster) return { entity: null, dist: Infinity }
      const objs = []
      this._scene.traverse(o => {
        if (o.isMesh && o.userData.pickable) objs.push(o)
      })
      const hits = this._raycaster.intersectObjects(objs, true)
      for (const h of hits) {
        if (hitHasLimbDragAncestor(h.object)) continue
        let o = h.object
        while (o && !o.userData.entityId) o = o.parent
        if (o && o.userData.entityId) {
          const ent = (this._entities || []).find(e => e.id === o.userData.entityId)
          if (ent) return { entity: ent, dist: h.distance }
        }
      }
      return { entity: null, dist: Infinity }
    },
    /** 最近命中的可拖拽肢体（假人） */
    pickLimbDragHit() {
      if (!this._scene || !this._raycaster) return null
      const objs = []
      this._scene.traverse(o => {
        if (o.isMesh && o.userData.pickable) objs.push(o)
      })
      const hits = this._raycaster.intersectObjects(objs, true)
      for (const h of hits) {
        let o = h.object
        let spec = null
        let entityId = null
        while (o) {
          if (!spec && o.userData && o.userData.limbDrag) spec = o.userData.limbDrag
          if (o.userData && o.userData.entityId) entityId = o.userData.entityId
          o = o.parent
        }
        if (!spec || !entityId) continue
        const ent = (this._entities || []).find(e => e.id === entityId)
        if (ent && ent.type === 'character' && ent.rig && ent.pose) return { entityId, spec }
      }
      return null
    },
    applyLimbDragDelta(ent, spec, dx, dy) {
      const pose = ent.pose
      if (!pose || !ent.rig) return
      if (spec.shoulderL) {
        pose.armLZ = THREE.MathUtils.clamp((pose.armLZ || 0) + dx * spec.mulZ, spec.minZ, spec.maxZ)
        pose.armLX = THREE.MathUtils.clamp((pose.armLX || 0) - dy * spec.mulX, spec.minX, spec.maxX)
      } else if (spec.shoulderR) {
        pose.armRZ = THREE.MathUtils.clamp((pose.armRZ || 0) + dx * spec.mulZ, spec.minZ, spec.maxZ)
        pose.armRX = THREE.MathUtils.clamp((pose.armRX || 0) - dy * spec.mulX, spec.minX, spec.maxX)
      } else {
        const delta = spec.useDX ? dx * spec.mul : -dy * spec.mul
        pose[spec.key] = THREE.MathUtils.clamp((pose[spec.key] || 0) + delta, spec.min, spec.max)
      }
      setRigPose(ent.rig, pose)
      if (this.selectedId === ent.id) Object.assign(this.edit.pose, pose)
    },
    onCanvasPointerDown(e) {
      if (this.lockScene || this.playing) return
      if (e.button !== 0) return
      if (!this.updateRaycasterFromClient(e.clientX, e.clientY)) return

      const limbHit = this.pickLimbDragHit()
      if (limbHit) {
        const ent = (this._entities || []).find(x => x.id === limbHit.entityId)
        if (ent) {
          this.selectEntity(ent.id)
          this._limbDrag = {
            entityId: limbHit.entityId,
            spec: limbHit.spec,
            lastX: e.clientX,
            lastY: e.clientY
          }
          if (this._controls) this._controls.enabled = false
          e.target.setPointerCapture(e.pointerId)
        }
        return
      }

      const { entity: hitEnt } = this.pickEntityForPositionDrag()

      if (hitEnt) {
        this.selectEntity(hitEnt.id)
        // 法线在 XZ 上投影：拖拽平面为竖直面（含世界 Y），竖直拖鼠标时 Y 会明显跟着变
        const n = new THREE.Vector3()
        this._editorCamera.getWorldDirection(n)
        n.y = 0
        if (n.lengthSq() < 1e-10) {
          n.set(1, 0, 0)
        } else {
          n.normalize()
        }
        const p0 = hitEnt.group.position.clone()
        this._drag = {
          id: hitEnt.id,
          plane: new THREE.Plane().setFromNormalAndCoplanarPoint(n, p0)
        }
        if (this._controls) this._controls.enabled = false
        e.target.setPointerCapture(e.pointerId)
      } else {
        this.selectedId = null
        ;(this._entities || []).forEach(en => clearEntityPickHighlight(en))
      }
    },
    onPointerMove(e) {
      if (!this._editorCamera || !this._raycaster || !this._pointerNDC) return
      if (this._limbDrag) {
        const d = this._limbDrag
        const ent = (this._entities || []).find(x => x.id === d.entityId)
        if (!ent || ent.type !== 'character') {
          this._limbDrag = null
          return
        }
        const dx = e.clientX - d.lastX
        const dy = e.clientY - d.lastY
        d.lastX = e.clientX
        d.lastY = e.clientY
        if (dx !== 0 || dy !== 0) this.applyLimbDragDelta(ent, d.spec, dx, dy)
        return
      }
      if (!this._drag) return
      const ent = (this._entities || []).find(x => x.id === this._drag.id)
      if (!ent) return
      if (!this.updateRaycasterFromClient(e.clientX, e.clientY)) return
      const pt = new THREE.Vector3()
      if (!this._raycaster.ray.intersectPlane(this._drag.plane, pt)) return
      ent.group.position.x = THREE.MathUtils.clamp(pt.x, -BOUNDS, BOUNDS)
      ent.group.position.y = THREE.MathUtils.clamp(pt.y, -0.5, 14)
      ent.group.position.z = THREE.MathUtils.clamp(pt.z, -BOUNDS, BOUNDS)
      this.syncEntityPanel()
      if (this.selectedId === ent.id) {
        this.edit.x = ent.group.position.x
        this.edit.y = ent.group.position.y
        this.edit.z = ent.group.position.z
      }
    },
    onPointerUp() {
      if ((this._drag || this._limbDrag) && this._controls) this._controls.enabled = !this.lockScene && !this.playing
      this._limbDrag = null
      this._drag = null
    },
    onKey(e) {
      if (e.code === 'Space') {
        e.preventDefault()
        if (!this.lockScene) this.togglePlay()
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedId && !this.lockScene) {
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
        e.preventDefault()
        this.removeSelected()
      }
    },
    teardownThree() {
      if (this._raf) cancelAnimationFrame(this._raf)
      this._raf = 0
      if (this._onWinResize) window.removeEventListener('resize', this._onWinResize)
      if (this._resizeObs) this._resizeObs.disconnect()
      if (this._controls) this._controls.dispose()
      ;(this._entities || []).forEach(e => {
        if (this._scene) this._scene.remove(e.group)
        disposeObject3D(e.group)
      })
      this._entities = []
      if (this._scene) {
        this.disposePanoramaMaps()
        if (this._grid) {
          this._scene.remove(this._grid)
          this._grid.geometry.dispose()
          this._grid.material.dispose()
        }
        if (this._axes) {
          this._scene.remove(this._axes)
          if (this._axes.geometry) this._axes.geometry.dispose()
          if (this._axes.material) this._axes.material.dispose()
        }
        disposeObject3D(this._scene)
        this._floor = null
      }
      if (this._pmrem) {
        this._pmrem.dispose()
        this._pmrem = null
      }
      if (this._exportRT) {
        this._exportRT.dispose()
        this._exportRT = null
      }
      if (this._renderer) this._renderer.dispose()
      this._renderer = null
      this._scene = null
      this._editorCamera = null
      this._shotCamera = null
      this._controls = null
      this._raycaster = null
      this._pointerNDC = null
      this._clock = null
    }
  }
}
</script>

<style scoped>
.ds-canvas {
  cursor: grab;
}
.ds-canvas:active {
  cursor: grabbing;
}
.ds-slider {
  height: 6px;
  accent-color: rgb(167 139 250);
}
.ds-slider:disabled {
  opacity: 0.35;
}
.shot-thirds {
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.14) 0, rgba(255, 255, 255, 0.14) 1px, transparent 1px),
    linear-gradient(to right, rgba(255, 255, 255, 0.14) 0, rgba(255, 255, 255, 0.14) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.14) 0, rgba(255, 255, 255, 0.14) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.14) 0, rgba(255, 255, 255, 0.14) 1px, transparent 1px);
  background-size:
    33.33% 100%,
    66.66% 100%,
    100% 33.33%,
    100% 66.66%;
  background-position: 0 0, 0 0, 0 0, 0 0;
  background-repeat: no-repeat;
}
</style>
